import { Injectable } from '@angular/core';
import { User } from '@mitel/cloudlink-sdk';
import { AppInjector } from '../../app-injector';
import { AdminService } from './admin.service';
import { SpinnerService } from './spinner.service';
import { HighLevelProductDetail, ProductLicense, ProductLicenseItemServiceInf } from '../interfaces/product-licenses-interfaces';
import { FeatureToggleService } from './feature-toggle.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from 'src/app/shared/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { ConfirmationDialogs } from 'src/app/shared/components/dialogs/confirmation-dialog/confirmation-dialogs.enum';
import { FeatureFlags } from '../interfaces/feature-flags';
import { MiCCBMinimumVersionForUsersIntegration, miccbUserStatus, productCodes } from '../constants/constants';
import { Util } from '../utils/Util';
import { MiccbTemplateConfigurationComponent, MiccbUserConfigurationComponent, asyncJobProcessingStatus, asyncJobStatus, EmployeeService, saveTypes } from '@mitel-internal/miccb-components';
import { Subject, Subscription } from 'rxjs';
import { AsyncJobsService } from './async-jobs.service';
import { asyncJobDataType, asyncJobOwner } from '../constants/constants';
import { MiccbAsyncJobData } from '../interfaces/async-job-data';
import { TranslateService } from '@ngx-translate/core';
import { Employee } from '@mitel-internal/miccb-components/lib/interfaces/employee';
import { MiccbApiService } from '@mitel-internal/miccb-components';
import { MiCCEmailService } from './micc-email.service';

@Injectable({
  providedIn: 'root'
})
export class UserProdMiCCBService implements ProductLicenseItemServiceInf {
  private adminService: AdminService;
  private spinnerService: SpinnerService;
  private featureToggleService: FeatureToggleService;
  private asyncJobsService: AsyncJobsService;
  private translateService: TranslateService;
  private employeeService: EmployeeService;
  private miccbApiService: MiccbApiService;
  private miccEmailService: MiCCEmailService;
  public dialog: MatDialog;
  subscription: Subscription;
  public updateProdNLcnsEvent = new Subject<any>();
  private loggedOnUserDefinedInMiCCB: miccbUserStatus = miccbUserStatus.UNKNOWN;

  constructor() { }

  initializeServices() {
    if (!this.adminService)
      this.adminService = AppInjector.injector.get(AdminService);

    if (!this.spinnerService)
      this.spinnerService = AppInjector.injector.get(SpinnerService);

    if (!this.featureToggleService)
      this.featureToggleService = AppInjector.injector.get(FeatureToggleService);

    if (!this.translateService)
      this.translateService = AppInjector.injector.get(TranslateService);

    if (!this.dialog)
      this.dialog = AppInjector.injector.get(MatDialog);

    if (!this.subscription)
      this.subscription = new Subscription();

    if (!this.asyncJobsService)
      this.asyncJobsService = AppInjector.injector.get(AsyncJobsService);

    if (!this.employeeService)
      this.employeeService = AppInjector.injector.get(EmployeeService);

    if (!this.miccbApiService)
      this.miccbApiService = AppInjector.injector.get(MiccbApiService);

    if (!this.miccEmailService)
      this.miccEmailService = AppInjector.injector.get(MiCCEmailService);
  }

  canBeAdded(user: User, product: ProductLicense, isSubComponent?: boolean): Promise<boolean> {
    return this.isLoggedOnUserDefinedInMiCCB(user?.accountId).then(result => {
      if (result === miccbUserStatus.USER_DEFINED_FULLACCESS) {
        return Promise.resolve(true);
      } else {
        return Promise.resolve(false);
      }
    });
  }

  async canBeTemplated(user: User): Promise<boolean> {
    return Promise.resolve(true);
  }

  canBeSelected(user: User): Promise<boolean> {
    // not concerned with licenses at this point
    // just have miccb feature toggled on + version 10.1.0.0 or higher + MiVB not installed
    this.initializeServices();
    return this.adminService.getAccountTags(user.accountId).then((tags: any) => {
      return (tags?.products?.indexOf(productCodes.MIVOICE_BUSINESS) === -1) && Util.doesAccountHaveMiCCBMinVersionX(tags, MiCCBMinimumVersionForUsersIntegration);
    })
  }

  hasRequiredAccountIntegrations( user: User, accountIntegrations: string[] ):  Promise<boolean>
  {
    return Promise.resolve( accountIntegrations.includes('MiCC') );
  }

  async getConfigurationDialog(setupComplete?: boolean, user?: any): Promise<any> {
    await this.initializeServices();
    if (!setupComplete) {
      return MiccbTemplateConfigurationComponent;
    } else {
      return undefined;
    }
  }

  async getConfigurationSubComponent(user: User) {
    await this.initializeServices();
    return Promise.resolve(MiccbUserConfigurationComponent);
  }

  async getConfigurationSubComponentDisplayName(user: User, componentInstance: any): Promise<string> {
    await this.initializeServices();
    return Promise.resolve('product_config.UserConfigurationComponent');
  }

  async getProductLicenseSection(user: User, setupComplete?: boolean): Promise<any> {
    await this.initializeServices();

    let section = {
      bundleCanBeEdit: false,
      bundleName: 'other',
      displayName: 'product_licenses.micontact_center_business',
      sectionName: 'product_licenses.micontact_center_business'};

    if (setupComplete) {
      section.bundleCanBeEdit = true;
      section.bundleName = 'miccb';
    }

    return Promise.resolve(section);

  }

  async initConfigurationSubComponent(componentInstance: any, users: User[], isReadOnly?: boolean, asyncJobId?: string) {
    await this.initializeServices();
    this.subscription.add(
      componentInstance.initialized.subscribe(async () =>{
        componentInstance.user = users[0];

        componentInstance.asyncJobId = asyncJobId ?? '';

        componentInstance.allInputsSentFromParent.next(true);
        console.log("All inputs sent to miccb user config");
      })
    );

    this.subscription.add(
      componentInstance.processing.subscribe(async (event) => {
        if (event?.status === asyncJobProcessingStatus.START) {
          this.spinnerService.showSpinner();
          this.spinnerService.setSpinnerMessageByKey('spinnerMessages.working');
          // Add the job
          this.asyncJobsService.removeAsyncJob(event?.asyncJobId);
          if (event.saveType && event.saveType === saveTypes.SAVE || event.saveType === saveTypes.SAVE_AND_DEPLOY) {
            let asyncJobData = {
              asyncJobId: event?.asyncJobId,
              userId: users[0]?.userId,
              owner: asyncJobOwner.MICCB,
              dismissible: false,
              type: asyncJobDataType.IN_PROGRESS,
              status: asyncJobStatus.IN_PROGRESS,
              httpMethod: event.method,
              createdOn: new Date(),
            } as MiccbAsyncJobData;
            asyncJobData.userName = await this.asyncJobsService.getUserName(asyncJobData.userId);
            asyncJobData.message = this.translateService.instant('async_jobs.users_edit.in_progress_alert', { user: asyncJobData.userName ?? asyncJobData.userId });
            this.asyncJobsService.addAsyncJob(asyncJobData);
          }

        } else if (event?.status === asyncJobProcessingStatus.IN_PROGRESS) {

          // Do not call  this.updateProdNLcnsEvent.next('inProgress');
          // as it will send us back to the user detail page to await async result.
          // We want to stay on current page until pseudo asyncJob is complete or failed

          this.spinnerService.hideSpinner();

          if (event.saveType && event.saveType === saveTypes.SAVE_AND_DEPLOY || event.saveType === saveTypes.DEPLOY) {
            try {
              // If a save is performed, wait for it it complete before sending the user email
              if (event.saveType === saveTypes.SAVE_AND_DEPLOY && event?.asyncJobId?.length > 0) {
                await this.asyncJobsService.pollAsyncJobStatusById(event.asyncJobId, 60000);
              }

              await this.employeeService.getEmployeeByCloudlinkId(users[0].accountId, event?.asyncJobId);
              let employee = this.employeeService.employee;
              if (employee === undefined) {
                console.error(`Failed to retrieve MiCC-B employee with cloudlinkId ${event?.asyncJobId}.  Deployment email cannot be sent.`);
                return;
              }
              await this.miccEmailService.deployMiCCUser(employee);

            } catch (error) {
              console.error("Error with MiCC-B deploy process for user", error);
              // We can process this as an error
              componentInstance.error?.next(new Error(`MiCC-B Deploy failed to send eMail: ${error} `));
              return;
            }
          }

        } else if (event?.status === asyncJobStatus.COMPLETE) {
          let job = this.asyncJobsService.getAsyncJobById(event.asyncJobId);
          if (!!job) job.status = asyncJobStatus.COMPLETE;
          this.updateProdNLcnsEvent.next(asyncJobStatus.COMPLETE);
          this.spinnerService.hideSpinner();
        } else if (event?.status === asyncJobStatus.FAILED) {
          let job = this.asyncJobsService.getAsyncJobById(event.asyncJobId);
          if (!!job) job.status = asyncJobStatus.FAILED;
          this.spinnerService.hideSpinner();
          // Do not update updateProdNLcnsEvent.next(), else the synchronous error banner (that has fail reason) will be overwritten by asyncJobs fail banner
        }

      })
    );

    this.subscription.add(
      componentInstance.saving.subscribe(async (state) => {
        if (state) {
          this.spinnerService.showSpinner();
          this.spinnerService.setSpinnerMessageByKey('spinnerMessages.working');
        } else {
          this.updateProdNLcnsEvent.next("resetList");
          this.spinnerService.hideSpinner();
        }
      })
    );


    this.subscription.add(
      componentInstance.error?.subscribe(error => {
        console.error("From Miccb component update user", error);
        this.spinnerService.hideSpinner();

        this.updateProdNLcnsEvent.next({ error: error });
      })
    );
    return Promise.resolve();
  }

  async initSecondaryComponent(user?: User) {
    return Promise.resolve(true);
  }

  async getSubComponentConfigurationDetails ( user: User, product?: ProductLicense ): Promise<any> {
    await this.initializeServices();
    let details: any = {
      subComponentNames: [],
      subComponentLabel: "product_licenses.features",
      subComponent: [],
      subComponentEditable: false
    }

    const result = await this.isLoggedOnUserDefinedInMiCCB(user?.accountId);

    if (result === miccbUserStatus.USER_DEFINED_READONLY || result === miccbUserStatus.USER_DEFINED_FULLACCESS) {

      await this.employeeService.getEmployeeByCloudlinkIdOrEmail(user.accountId, user.userId, user.email);
      const employee = this.employeeService.employee;
      if (!!employee && employee.id) {
        details.subComponentEditable = true;
        details.subComponentLabel = employee.isActive ? this.translateService.instant('miccb-details.contactCenterServiceDetails') : this.translateService.instant('miccb-details.contactCenterServiceDetailsUserInactive');

        let serviceDetails = [];
        if (employee.reporting) {
          serviceDetails.push(this.translateService.instant('miccb-details.reportingNumber', { value: employee.reporting }));
        }
        const mediaTypeString = this.formatMediaTypes(employee);
        if (mediaTypeString.length > 0) {
          serviceDetails.push(mediaTypeString)
        }
        if (employee.phoneTypeLicense) {
          serviceDetails.push(this.translateService.instant('miccb-details.phoneLicenseType', { value: employee.phoneTypeLicense }));
        }
        if (employee.supervisorLicenseType) {
          serviceDetails.push(this.translateService.instant('miccb-details.supervisorLicenseType', { value: employee.supervisorLicenseType }));
        }

        if (serviceDetails.length > 0) {
          details.highLevelProductDetails = serviceDetails.map(d => (<HighLevelProductDetail>{ primary: d, secondaries: [] }) ) ?? [];
        }
      }
    } else {
      details.subComponentEditable = true;
      details.subComponentLabel = this.translateService.instant('miccb-details.contactCenterServiceDetails');
      const msg = (result === miccbUserStatus.USER_UNDEFINED) ? 'miccb-details.insufficientPermission' : 'miccb-details.miccbConnectionUnavailable';
      details.highLevelProductDetails = [{ primary: this.translateService.instant(msg), secondaries: [] }];
    }

    return Promise.resolve( details );

  }

  async getHighLevelProductDetails(user: User): Promise<HighLevelProductDetail[]> {
    return this.getSubComponentConfigurationDetails(user)
      .then(result => result.highLevelProductDetails ?? []);
  }

  async isSetupComplete(user: User): Promise<boolean> {
    this.initializeServices();

    // If the logged on user is not defined in MiCCB, report setup as complete since the user can't access the employee info in MiCCB.
    // This avoids displaying "Complete Setup" under Products & Licenses
    const result = await this.isLoggedOnUserDefinedInMiCCB(user?.accountId);

    if ([miccbUserStatus.USER_UNDEFINED, miccbUserStatus.LINK_DOWN].includes(result)) {
      console.log('UserProdMiCCBService: isSetupComplete - returning true due to undefined user or MICC-B link down');
      return true;
    }

    await this.employeeService.getEmployeeByCloudlinkIdOrEmail(user.accountId, user.userId, user.email);
    const employee = this.employeeService.employee;
    if (!!employee && employee.id) {
      return true;
    } else {
      return false;
    }
  }

  getUpdateProdNLcnsSecEventFromConfigComp() {
    return this.updateProdNLcnsEvent;
  }

  templateHasProduct(template: any) {
    return template?.pbxConfig?.cceServiceTemplate ? true : false;
  }

  getTemplateBadges(template: any) {
    return [];
  }

  handleProductStateChange(user: User, state: boolean, isSubComponent?: boolean): Promise<any> {
    this.initializeServices();

    // user coming in may be out of date - get a fresh copy
    return this.adminService.getUserWithoutFrills(user.userId, 'tags').then(async freshUserWithTags => {
      let tags = freshUserWithTags.tags || {};
      console.log("UserWithTags for miccb state change: ", freshUserWithTags);

      let tagBody = {
        products: []
      };
      if (state) {
        if (tags && tags.products) {
          tagBody.products.push(...tags.products);
        }
        let index = tagBody.products.indexOf('miccb');
        if (index === -1) {
          tagBody.products.push('miccb');
        }

        // Not sure who is looking for this so we will keep for now. In the future we should consider removing this check and just checking the product tags
        if (!tags.MiCCBusiness) {
          tagBody["MiCCBusiness"] = {
            ChatEnabled: false
          }
        }

        let updatedTags = await this.adminService.tryUpdateTagsOfUserByAccountId(freshUserWithTags.accountId, freshUserWithTags.userId, tagBody, tags);
        console.log('updated tags after miccb state change', updatedTags);
      } else {
        if (tags && tags.products) {
          tagBody.products.push(...tags.products);
        }
        let index = tagBody.products.indexOf('miccb');
        if (index > -1) {
          tagBody.products.splice(index, 1);
        }

        await this.adminService.deleteUserTagByAccountId(freshUserWithTags.accountId, freshUserWithTags.userId, 'MiCCBusiness');
        await this.adminService.tryUpdateTagsOfUserByAccountId(user.accountId, user.userId, tagBody, tags);
        await this.deactivateMiCCBEmployee(user.accountId, user.userId, user.email);
      }
    });
  }

  // This determines whether Add/Remove is enabled in the Add Product dialog opened from Products and licenses
  // If the logged on user isn't defined in MiCC with full access, don't allow this
  isButtonDisabled(user: User, product: ProductLicense, isSubComponent?: boolean): Promise<boolean> {

    return this.isLoggedOnUserDefinedInMiCCB(user?.accountId).then(result => {
      if (result === miccbUserStatus.USER_DEFINED_FULLACCESS) {
        return Promise.resolve(false);
      } else {
        return Promise.resolve(true);
      }
    });
  }

  async isProductEnabled(user: User, isSubComponent?: boolean): Promise<boolean> {
    this.initializeServices();

    return this.adminService.getUserTagsByAccountIdWithoutSpinner(user.accountId, user.userId).then(tags => {
      if (tags.MiCCBusiness) {
        return true;
      } else {
        return false;
      }
    });
  }

  getRemainingLicenses?(): Promise<number> {
    return Promise.resolve(1000); // Temporary passing 1000 for testing.
  }

  canDeleteProduct(user: User): Promise<boolean> {
    this.initializeServices();

    return this.isLoggedOnUserDefinedInMiCCB(user?.accountId).then(result => {
      this.spinnerService.hideSpinner();
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, { data: { mode: (result === miccbUserStatus.USER_DEFINED_FULLACCESS) ? ConfirmationDialogs.REMOVE_MICCB_USER_PRODUCT_CONFIRMATION : ConfirmationDialogs.REMOVE_MICCB_USER_PRODUCT_LIMITEDACCESS_CONFIRMATION } });
      return dialogRef.afterClosed().toPromise().then(result => {
        return result;
      });
    });
  }

  isWideConfig() {
    return true;
  }

  shouldShowWarning(): boolean {
    return [miccbUserStatus.USER_UNDEFINED, miccbUserStatus.LINK_DOWN].includes(this.loggedOnUserDefinedInMiCCB);
  }

  getWarningTooltip() {
    if (this.loggedOnUserDefinedInMiCCB === miccbUserStatus.USER_UNDEFINED) {
      return 'miccb-details.insufficientPermission';
    } else {
      return 'miccb-details.miccbConnectionUnavailable';
    }
  }

  formatMediaTypes(employee: Employee): string {
    let formattedString = '';
    if (!!employee) {
      if (employee.isEnabledForVoice) {
        formattedString += this.translateService.instant('miccb-details.voice');
      }

      if (employee.isEnabledForEmail) {
        if (formattedString.length > 0) {
          formattedString += ' | ';
        }
        formattedString += this.translateService.instant('miccb-details.email');
      }

      if (employee.isEnabledForSMS) {
        if (formattedString.length > 0) {
          formattedString += ' | ';
        }
        formattedString += this.translateService.instant('miccb-details.sms');
      }

      if (employee.isEnabledForChat) {
        if (formattedString.length > 0) {
          formattedString += ' | ';
        }
        formattedString += this.translateService.instant('miccb-details.chat');
      }

      if (employee.isEnabledForOpenMedia) {
        if (formattedString.length > 0) {
          formattedString += ' | ';
        }
        formattedString += this.translateService.instant('miccb-details.open');
      }

      if (formattedString.length > 0) {
        formattedString = this.translateService.instant('miccb-details.mediaServers', { value: formattedString });
      }
    }

    return formattedString;
  }

  /**
   * Checks feature flags, and has MiCC-B with supported version
   * @param accountId
   * @returns
   */
  async isMiCCBEnabled(accountId: string): Promise<boolean> {
    try {
      this.initializeServices();
      const tags = await this.adminService.getAccountTags(accountId);
      return this.isMiCCBEnabledByTags(tags);
    } catch (error) {
      console.warn('isMiCCBEnabled() failed', error);
      return false;
    }
  }

  /**
   * Like isMiCCBEnabled
   * @param accountTags
   * @returns
   */
  isMiCCBEnabledByTags(accountTags: any): boolean {
    this.initializeServices();
    return Util.doesAccountHaveMiCCBMinVersionX(accountTags, MiCCBMinimumVersionForUsersIntegration);
  }

  /**
 * Return the logged on user's credentials in MiCC-B to determine whether MiCC-B info can be accessed
 * Cached value is returned if available.
 * Provide accountTags or accountId to determine if MiCCB is installed and enabled.
 */
  async isLoggedOnUserDefinedInMiCCB(accountId: string, accountTags?: any): Promise<miccbUserStatus> {
    // return cached value if available
    if (this.loggedOnUserDefinedInMiCCB !== miccbUserStatus.UNKNOWN) {
      console.log('isLoggedOnUserDefinedInMiCCB - returning cached value', this.loggedOnUserDefinedInMiCCB);
      return this.loggedOnUserDefinedInMiCCB;
    }

    this.initializeServices();

    try {
      let enabled = false;
      let miccbStatus = miccbUserStatus.USER_UNDEFINED;

      if (!!accountTags) {
        enabled = this.isMiCCBEnabledByTags(accountTags);
      } else if (!!accountId) {
        enabled = await this.isMiCCBEnabled(accountId);
      }

      if (enabled) {
        const result = await this.miccbApiService.getEmployee(accountId, 'me').toPromise();

        if (!!result) {

          // Ensure that the supervisorLicenseType is Premium; otherwise, MiCC APIs can't be accessed so treat the user as undefined
          if (result.supervisorLicenseType === 'Premium') {
            miccbStatus = miccbUserStatus.USER_DEFINED_READONLY;

            // Check the user's security role in MiCC-B
            if (!!result.securityRoleRefId) {
              const securityRoleResult = await this.miccbApiService.getSecurityRole(accountId, result.securityRoleRefId).toPromise();
              if (!!securityRoleResult) {

                const securityRole: number = securityRoleResult?.mayManageDeviceConfiguration;
                if (securityRole >= 2) {
                  miccbStatus = miccbUserStatus.USER_DEFINED_FULLACCESS;
                }
              }
            }
          }
        }
      }
      this.loggedOnUserDefinedInMiCCB = miccbStatus;
    } catch (error) {
      console.error(`isLoggedOnUserDefinedInMiCCB:error=${error.status}`, error);
      if (error.status === 401) {
        // Indicates user is not defined in MiCC-B or doesn't have access to the data
        this.loggedOnUserDefinedInMiCCB = miccbUserStatus.USER_UNDEFINED;
      } else {
        // Other errors indicate the tunnel connection is down
        this.loggedOnUserDefinedInMiCCB = miccbUserStatus.LINK_DOWN;
      }
    }

    console.info(`isLoggedOnUserDefinedInMiCCB=${this.loggedOnUserDefinedInMiCCB}`);
    return this.loggedOnUserDefinedInMiCCB;
  }

  async deactivateMiCCBEmployee(accountId: string, userId: string, email: string): Promise<boolean> {

    this.initializeServices();

    await this.employeeService.getEmployeeByCloudlinkIdOrEmail(accountId, userId, email);
    let employee = this.employeeService.employee;
    if (!!employee) {
      const reqBody = [
        {
          id: employee.id,
          isRealTime: false,
          isActive: false,
          isEnabledForVoice: false,
          isEnabledForEmail: false,
          isEnabledForChat: false,
          isEnabledForSMS: false,
          isEnabledForOpenMedia: false
        }
      ];
      await this.employeeService.updateEmployee(accountId, employee.id, reqBody);
      employee = this.employeeService.employee;
      if (!!employee && employee.isActive === false) {
        console.log(`successfully deactivated user ${userId} in MiCCB`);
        return true;
      } else {
        console.error(`failed to deactivate user ${userId} in MiCCB`);
        return false;
      }
    }

    // User doesn't exist in MiCC-B
    return true;
  }

  /**
   * Needed if we want to pass data to miccb-components/MiccbTemplateConfigurationComponent.
   * See this.getConfigurationDialog()
   * @param user
   * @param data
   * @returns
   */
  getInputDataForConfigurationDialog(user: User, data?: any): Promise<any> {
    return Promise.resolve(data);
  }
}
